import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { checkDuplicate, extractMerchantPattern, type ExistingTransaction } from '@/lib/duplicate-detection';

export type ImportStep = 'upload' | 'processing' | 'preview' | 'complete';

export interface ProcessedTransaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  type: 'income' | 'expense';
  category_id: string | null;
  category_name: string | null;
  category_suggestion?: string;
  confidence: 'high' | 'medium' | 'low';
  auto_mapped: boolean;
  installment_info?: string;
  is_installment: boolean;
  selected: boolean;
  is_duplicate?: boolean;
  duplicate_similarity?: number;
}

export interface ImportSummary {
  total: number;
  income: number;
  expense: number;
  auto_categorized: number;
  suggested: number;
  uncategorized: number;
}

export interface ImportResult {
  success: boolean;
  file_name: string;
  file_hash: string;
  already_imported: boolean;
  previous_import_date?: string;
  bank_name?: string;
  statement_period?: { start: string; end: string };
  credit_card_invoice_date?: string | null;
  is_credit_card?: boolean;
  transactions: ProcessedTransaction[];
  summary: ImportSummary;
}

// Get Supabase URL from environment for direct fetch calls
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

export function useImportStatement() {
  const [step, setStep] = useState<ImportStep>('upload');
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [transactions, setTransactions] = useState<ProcessedTransaction[]>([]);
  const [importStats, setImportStats] = useState({ imported: 0, skipped: 0, mappingsCreated: 0 });
  const { toast } = useToast();

  const processFile = async (file: File, accountId: string) => {
    setIsProcessing(true);
    setStep('processing');

    try {
      // Force refresh to get a valid token
      const { data: { session }, error: sessionError } = await supabase.auth.refreshSession();
      if (sessionError || !session) {
        // Fallback to getSession if refresh fails
        const { data: fallbackData } = await supabase.auth.getSession();
        if (!fallbackData.session) {
          throw new Error('Not authenticated');
        }
      }
      
      const currentSession = session || (await supabase.auth.getSession()).data.session;
      if (!currentSession) {
        throw new Error('Not authenticated');
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('accountId', accountId);

      // Use native fetch instead of supabase.functions.invoke
      // because invoke() doesn't properly handle FormData with auth headers
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/process-bank-statement`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${currentSession.access_token}`,
            'apikey': SUPABASE_ANON_KEY,
          },
          body: formData, // Don't set Content-Type, browser sets it with boundary
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP ${response.status}`);
      }

      const data = await response.json() as ImportResult;
      
      if (!data.success) {
        throw new Error('Failed to process statement');
      }

      // Check for duplicates against existing transactions
      const { data: existingTransactions } = await supabase
        .from('transactions')
        .select('id, description, amount, transaction_date')
        .eq('account_id', accountId);

      const transactionsWithDuplicates = data.transactions.map(tx => {
        const duplicateCheck = checkDuplicate(
          { description: tx.description, amount: tx.amount, date: tx.date },
          (existingTransactions as ExistingTransaction[]) || []
        );

        return {
          ...tx,
          is_duplicate: duplicateCheck.isDuplicate,
          duplicate_similarity: duplicateCheck.similarity,
          selected: !duplicateCheck.isDuplicate // Auto-deselect duplicates
        };
      });

      setResult({ ...data, transactions: transactionsWithDuplicates });
      setTransactions(transactionsWithDuplicates);
      setStep('preview');

    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: 'Erro ao processar arquivo',
        description: error instanceof Error ? error.message : 'Tente novamente',
        variant: 'destructive'
      });
      setStep('upload');
    } finally {
      setIsProcessing(false);
    }
  };

  const toggleTransaction = (id: string) => {
    setTransactions(prev => 
      prev.map(tx => tx.id === id ? { ...tx, selected: !tx.selected } : tx)
    );
  };

  const selectAll = () => {
    setTransactions(prev => prev.map(tx => ({ ...tx, selected: true })));
  };

  const deselectAll = () => {
    setTransactions(prev => prev.map(tx => ({ ...tx, selected: false })));
  };

  const deselectDuplicates = () => {
    setTransactions(prev => 
      prev.map(tx => tx.is_duplicate ? { ...tx, selected: false } : tx)
    );
  };

  const updateTransactionCategory = (id: string, categoryId: string, categoryName: string) => {
    setTransactions(prev =>
      prev.map(tx => 
        tx.id === id 
          ? { ...tx, category_id: categoryId, category_name: categoryName, auto_mapped: false }
          : tx
      )
    );
  };

  const confirmImport = async (accountId: string) => {
    setIsProcessing(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const selectedTransactions = transactions.filter(tx => tx.selected);
      let imported = 0;
      let mappingsCreated = 0;

      // Get credit_card_invoice_date from the import result
      const creditCardInvoiceDate = result?.credit_card_invoice_date || null;

      // Insert transactions
      for (const tx of selectedTransactions) {
        // Validate and sanitize the amount
        const amount = Math.abs(Number(tx.amount));
        if (isNaN(amount) || amount <= 0) {
          console.error('Invalid amount for transaction:', tx);
          continue;
        }

        // Determine transaction type - default to 'expense' if undefined
        const txType = tx.type || 'expense';

        const transactionData: Record<string, any> = {
          user_id: user.id,
          account_id: accountId,
          category_id: tx.category_id || null,
          description: tx.description.trim(),
          amount: amount,
          type: txType as 'income' | 'expense',
          transaction_date: tx.date,
          is_paid: new Date(tx.date) <= new Date(),
          is_recurring: tx.is_installment || false,
          notes: tx.installment_info ? `Parcela ${tx.installment_info}` : null
        };

        // Add credit_card_invoice_date for credit card transactions
        if (creditCardInvoiceDate) {
          transactionData.credit_card_invoice_date = creditCardInvoiceDate;
        }

        const { error } = await supabase.from('transactions').insert(transactionData);

        if (error) {
          console.error('Error inserting transaction:', error.message, error.details);
        } else {
          imported++;

          // Create/update category mapping if manually categorized
          if (tx.category_id && !tx.auto_mapped) {
            const pattern = extractMerchantPattern(tx.description);
            if (pattern) {
              const { data: existing } = await supabase
                .from('category_mappings')
                .select('id, match_count')
                .eq('merchant_pattern', pattern)
                .maybeSingle();

              if (existing) {
                await supabase
                  .from('category_mappings')
                  .update({ 
                    category_id: tx.category_id,
                    match_count: existing.match_count + 1,
                    confidence: Math.min(1, 0.5 + (existing.match_count + 1) * 0.1)
                  })
                  .eq('id', existing.id);
              } else {
                const { error: mappingError } = await supabase
                  .from('category_mappings')
                  .insert({
                    user_id: user.id,
                    merchant_pattern: pattern,
                    category_id: tx.category_id
                  });
                
                if (!mappingError) mappingsCreated++;
              }
            }
          }
        }
      }

      // Record import history
      if (result && imported > 0) {
        const { error: historyError } = await supabase.from('import_history').insert({
          user_id: user.id,
          account_id: accountId,
          file_name: result.file_name,
          file_hash: result.file_hash,
          transactions_count: imported,
          period_start: result.statement_period?.start || null,
          period_end: result.statement_period?.end || null
        });
        
        if (historyError) {
          console.error('Error recording import history:', historyError);
        }
      }

      setImportStats({
        imported,
        skipped: transactions.length - selectedTransactions.length,
        mappingsCreated
      });

      if (imported === 0 && selectedTransactions.length > 0) {
        toast({
          title: 'Erro ao importar',
          description: 'Nenhuma transação foi importada. Verifique o console para mais detalhes.',
          variant: 'destructive'
        });
        return;
      }

      setStep('complete');
      toast({
        title: 'Importação concluída!',
        description: `${imported} transações importadas com sucesso.`
      });

    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: 'Erro ao importar',
        description: error instanceof Error ? error.message : 'Tente novamente',
        variant: 'destructive'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const reset = () => {
    setStep('upload');
    setResult(null);
    setTransactions([]);
    setImportStats({ imported: 0, skipped: 0, mappingsCreated: 0 });
  };

  return {
    step,
    isProcessing,
    result,
    transactions,
    importStats,
    processFile,
    toggleTransaction,
    selectAll,
    deselectAll,
    deselectDuplicates,
    updateTransactionCategory,
    confirmImport,
    reset
  };
}
